/**
 * PokéAPI Integration Layer with Advanced Caching & Error Handling
 * Handles all interactions with the PokéAPI service
 */

import type { Pokemon, PokemonStat } from '../types/pokemon';
import { pokemonCache } from '../utils/cache';

const BASE_URL = 'https://pokeapi.co/api/v2';
const MAX_POKEMON_ID = 1025; // Total number of Pokémon across all generations (as of Gen 9)
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

interface PokemonApiResponse {
  id: number;
  name: string;
  types: Array<{ type: { name: string } }>;
  sprites: {
    other: {
      'official-artwork': {
        front_default: string;
        front_shiny: string;
      };
    };
    versions: {
      'generation-v': {
        'black-white': {
          animated?: {
            front_default: string;
          };
        };
      };
    };
    front_default: string;
  };
  height: number;
  weight: number;
  stats: Array<{
    stat: { name: string };
    base_stat: number;
  }>;
}

interface SpeciesApiResponse {
  names: Array<{ language: { name: string }; name: string }>;
  genera: Array<{ language: { name: string }; genus: string }>;
  flavor_text_entries: Array<{ language: { name: string }; flavor_text: string; version: { name: string } }>;
  generation: { url: string };
  egg_groups: Array<{ name: string }>;
}

const EGG_GROUP_TRANSLATIONS: Record<string, string> = {
  'monster': 'Monstru',
  'water1': 'Eau 1',
  'water2': 'Eau 2',
  'water3': 'Eau 3',
  'bug': 'Insecte',
  'flying': 'Aérien',
  'field': 'Terrestre',
  'fairy': 'Féérique',
  'grass': 'Végétal',
  'human-like': 'Humanoïde',
  'mineral': 'Minéral',
  'amorphous': 'Amorphe',
  'dragon': 'Dragon',
  'ditto': 'Métamorph',
  'no-eggs': 'Sans Œufs',
  'undiscovered': 'Inconnu',
};

/**
 * Delays execution for retry logic
 */
const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetches data with retry logic
 */
async function fetchWithRetry<T>(
  url: string,
  retries = MAX_RETRIES
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');

      if (attempt < retries - 1) {
        await delay(RETRY_DELAY * (attempt + 1)); // Exponential backoff
      }
    }
  }

  throw lastError || new Error('Failed to fetch after retries');
}

/**
 * Fetches Pokémon data by ID including French name
 * Uses cache to avoid repeated API calls
 */
export async function fetchPokemonById(id: number): Promise<Pokemon> {
  // Validate ID
  if (id < 1 || id > MAX_POKEMON_ID) {
    throw new Error(`Invalid Pokémon ID: ${id}. Must be between 1 and ${MAX_POKEMON_ID}`);
  }

  // Check cache first
  const cacheKey = `pokemon-${id}`;
  const cached = pokemonCache.get(cacheKey);

  if (cached) {
    return cached as Pokemon;
  }

  try {
    // Fetch both pokemon data and species data in parallel
    const [pokemonData, speciesData] = await Promise.all([
      fetchWithRetry<PokemonApiResponse>(`${BASE_URL}/pokemon/${id}`),
      fetchWithRetry<SpeciesApiResponse>(`${BASE_URL}/pokemon-species/${id}`),
    ]);

    // Extract French name and category
    const frenchName =
      speciesData.names.find(n => n.language.name === 'fr')?.name ||
      pokemonData.name;

    const frenchGenus =
      speciesData.genera.find(g => g.language.name === 'fr')?.genus ||
      'Pokémon';

    // Extract French Pokedex description (clean up newlines/form feeds)
    const frenchFlavorText =
      speciesData.flavor_text_entries
        .filter(f => f.language.name === 'fr')
        .map(f => f.flavor_text.replace(/[\n\f\r]/g, ' ').replace(/\s+/g, ' ').trim())
        .pop() || '';

    // Extract generation (roman numeral to number)
    const generationUrl = speciesData.generation.url;
    const generationNumber = parseInt(
      generationUrl.split('/').slice(-2, -1)[0]
    );

    // Extract egg groups (translated to French)
    const eggGroups = speciesData.egg_groups.map(eg =>
      EGG_GROUP_TRANSLATIONS[eg.name] || eg.name
    );

    // Transform stats
    const stats: PokemonStat[] = pokemonData.stats.map(s => ({
      name: s.stat.name,
      value: s.base_stat,
    }));

    const pokemon: Pokemon = {
      id: pokemonData.id,
      name: pokemonData.name,
      nameFr: frenchName,
      category: frenchGenus,
      description: frenchFlavorText,
      generation: generationNumber,
      eggGroups,
      types: pokemonData.types.map(t => t.type.name),
      sprite: pokemonData.sprites.other['official-artwork'].front_default,
      spriteShiny: pokemonData.sprites.other['official-artwork'].front_shiny,
      spriteAnimated:
        pokemonData.sprites.versions['generation-v']['black-white'].animated?.front_default ||
        pokemonData.sprites.front_default,
      height: pokemonData.height, // in decimeters
      weight: pokemonData.weight, // in hectograms
      stats,
    };

    // Cache the result
    pokemonCache.set(cacheKey, pokemon);

    return pokemon;
  } catch (error) {
    console.error(`Error fetching Pokémon #${id}:`, error);
    throw new Error(
      `Failed to fetch Pokémon #${id}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Fetches multiple Pokémon by IDs with optimized parallel fetching
 * Uses Promise.all for maximum performance
 */
export async function fetchMultiplePokemon(ids: number[]): Promise<Pokemon[]> {
  if (ids.length === 0) {
    return [];
  }

  try {
    // Fetch all Pokemon in parallel
    const promises = ids.map(id => fetchPokemonById(id));
    const results = await Promise.allSettled(promises);

    // Filter out failed requests and log errors
    const pokemon: Pokemon[] = [];
    const errors: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        pokemon.push(result.value);
      } else {
        errors.push(`Pokémon ID ${ids[index]}: ${result.reason.message}`);
      }
    });

    // Log errors if any
    if (errors.length > 0) {
      console.error('Some Pokémon failed to fetch:', errors);
    }

    return pokemon;
  } catch (error) {
    console.error('Error fetching multiple Pokémon:', error);
    throw new Error(
      `Failed to fetch multiple Pokémon: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Gets the maximum Pokémon ID available
 */
export function getMaxPokemonId(): number {
  return MAX_POKEMON_ID;
}

/**
 * Clears the Pokemon cache
 */
export function clearCache(): void {
  pokemonCache.clear();
}

/**
 * Gets cache statistics
 */
export function getCacheStats() {
  return pokemonCache.getStats();
}

/**
 * Preloads multiple Pokemon into cache
 * Useful for starter Pokemon or frequently accessed Pokemon
 */
export async function preloadPokemon(ids: number[]): Promise<void> {
  try {
    await fetchMultiplePokemon(ids);
  } catch (error) {
    console.error('Failed to preload Pokemon:', error);
  }
}
